import React, { useState, useEffect } from "react";
import { Flex, Text, Select, IconButton, Button, Dialog } from "@radix-ui/themes";
import { ArrowRightIcon } from "@radix-ui/react-icons";
import { ActiveIntegrateCard } from "components";
import { PermissionAlertDialog } from "components";
import { 
  useGetGoogleLocationMappings, 
  useGetGoogleLocations, 
  useSaveGoogleLocationMappings, 
  useDeactivateGoogleReviews
} from "hooks";
import { useQueryClient } from "@tanstack/react-query";
import { useProfileContext, useLocationContext } from "context";
import { useToast } from "../../../context/ToastContext";

export function ActiveGoogleReviewsIntegration() {
  const queryClient = useQueryClient();
  const { userProfile } = useProfileContext();
  const { initialItems } = useLocationContext();
  const { setToastMessage } = useToast();
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showPermissionAlert, setShowPermissionAlert] = useState(false);
  const [isDeactivating, setIsDeactivating] = useState(false);

  // State for mappings
  const [locationMappings, setLocationMappings] = useState({});
  const [googleLocations, setGoogleLocations] = useState([]);
  const [allLocations, setAllLocations] = useState([]);
  const [isUpdating, setIsUpdating] = useState(false);

  // Get existing mappings and Google locations
  const { data: mappingsData, isLoading: isMappingsLoading } = useGetGoogleLocationMappings(userProfile?.organisation_id);
  const { data: googleLocationsData, isLoading: isLocationsLoading } = useGetGoogleLocations(
    userProfile?.organisation_id,
    !!userProfile?.organisation_id
  );

  // Mutations
  const saveLocationMappings = useSaveGoogleLocationMappings();
  const deactivateGoogleReviews = useDeactivateGoogleReviews();

  // Check if user is owner
  const isOwner = userProfile.user_role === "Owner";

  // Handle disconnect click with permission check
  const handleDisconnectClick = () => {
    if (isOwner) {
      setShowConfirmDialog(true);
    } else {
      setShowPermissionAlert(true);
    }
  };

  // Handle deactivation
  const handleDeactivate = async () => {
    try {
      setIsDeactivating(true);

      const result = await deactivateGoogleReviews.mutateAsync({
        organisation_id: userProfile.organisation_id,
      });

      if (result?.data?.status === "success") {
        // Invalidate the status query to refresh the active integrations list
        queryClient.invalidateQueries(["google-reviews-status"]);
        queryClient.invalidateQueries(["google-location-mappings"]);
        queryClient.removeQueries({ queryKey: ["review-competitors"] });
        setToastMessage("Google Reviews integration disconnected successfully");
      } else {
        throw new Error(result?.data?.message || "Failed to disconnect Google Reviews integration");
      }
    } catch (error) {
      console.error("Error deactivating Google Reviews:", error);
      setToastMessage(error.message || "Failed to disconnect Google Reviews integration");
    } finally {
      setIsDeactivating(false);
      setShowConfirmDialog(false);
    }
  };

  // Effect to set Google locations when API data is available
  useEffect(() => {
    if (googleLocationsData?.data?.status === "success" && googleLocationsData?.data?.accounts) {
      const allGoogleLocs = [];
      googleLocationsData.data.accounts.forEach((account) => {
        if (account.locations && Array.isArray(account.locations)) {
          account.locations.forEach((loc) => {
            // Format address from storefrontAddress
            let formattedAddress = "";
            if (loc.storefrontAddress) {
              const addr = loc.storefrontAddress;
              const parts = [];
              if (addr.addressLines) parts.push(...addr.addressLines);
              if (addr.locality) parts.push(addr.locality);
              if (addr.postalCode) parts.push(addr.postalCode);
              formattedAddress = parts.join(", ");
            }

            allGoogleLocs.push({
              name: `${account.account_id}/${loc.name}`,
              title: loc.title || loc.name,
              address: formattedAddress,
              accountId: account.account_id.replace("accounts/", ""),
              locationId: loc.name.replace("locations/", ""),
              fullLocationId: `locations/${loc.name.replace("locations/", "")}`,
              placeId: loc.metadata?.placeId || "",
            });
          });
        }
      });
      setGoogleLocations(allGoogleLocs);
    }
  }, [googleLocationsData]);

  // Effect to process existing mappings
  useEffect(() => {
    if (mappingsData?.data?.mappings && initialItems) {
      const mappings = mappingsData.data.mappings;
      const locToGoogleLoc = {};

      // Process each mapping
      mappings.forEach((mapping) => {
        locToGoogleLoc[mapping.location] = mapping.google_business_location_id;
      });

      setLocationMappings(locToGoogleLoc);

      // Combine all locations with mapping information
      const locations = initialItems.map((loc) => {
        const mapping = mappings.find((m) => m.location.toString() === loc.value.toString());
        return {
          ...loc,
          isMapped: !!mapping,
          google_location_name: mapping?.google_location_name || "",
        };
      });

      setAllLocations(locations);
    }
  }, [mappingsData, initialItems]);

  // Handle Google location selection change
  const handleGoogleLocationChange = (locationId, googleLocationName) => {
    if (googleLocationName === "none") {
      const newMappings = { ...locationMappings };
      delete newMappings[locationId];
      setLocationMappings(newMappings);
    } else {
      setLocationMappings({
        ...locationMappings,
        [locationId]: googleLocationName,
      });
    }
  };

  // Save updated mappings
  const handleSaveMappings = async () => {
    try {
      setIsUpdating(true);

      // Prepare location mappings data - only for unmapped locations
      const mappingsToSave = allLocations
        .filter((loc) => !loc.isMapped && locationMappings[loc.value])
        .map((loc) => {
          const googleLocationName = locationMappings[loc.value];
          const googleLoc = googleLocations.find((g) => g.name === googleLocationName);
          if (!googleLoc) return null;

          return {
            location_id: parseInt(loc.value),
            google_location_name: googleLoc.title,
            google_location_address: googleLoc.address || "",
            google_business_account_id: `accounts/${googleLoc.accountId}`,
            google_business_location_id: `locations/${googleLoc.locationId}`,
            google_place_id: googleLoc.placeId || "",
          };
        })
        .filter(Boolean);

      // Skip if no mappings to save
      if (mappingsToSave.length === 0) {
        setToastMessage("No changes to save");
        setIsUpdating(false);
        return;
      }

      // Call the save-mappings API
      const result = await saveLocationMappings.mutateAsync({
        organisation_id: userProfile.organisation_id,
        mappings: mappingsToSave,
      });

      if (result?.data?.status !== "success") {
        throw new Error(result?.data?.message || "Failed to save mappings");
      }

      // Refresh data
      queryClient.invalidateQueries(["google-location-mappings"]);
      queryClient.invalidateQueries(["google-reviews-status"]);
      setToastMessage("Mappings saved successfully");
    } catch (error) {
      console.error("Error saving mappings:", error);
      setToastMessage(error.message || "Failed to save mappings");
    } finally {
      setIsUpdating(false);
    }
  };

  // Loading state
  if (isMappingsLoading || isLocationsLoading) {
    return (
      <Flex direction="column" align="center" justify="center" style={{ height: "200px" }}>
        <Text>Loading Google Reviews integration data...</Text>
      </Flex>
    );
  }

  return (
    <>
      <ActiveIntegrateCard.Root
        name="Google Reviews"
        subtitle="Customer reviews"
        image_url="google.png"
        option={{
          text: "Disconnect",
          color: "red",
          onClick: handleDisconnectClick,
        }}
      >
        {/* Location Mappings Section */}
        <ActiveIntegrateCard.Section
          name="Location Mapping"
          subtitle="Map each b connect location to its corresponding Google Business Profile location. Reviews will be synced automatically for mapped locations."
        >
          <Flex direction="column" gap="4" style={{ width: "100%" }}>
            {/* Header row */}
            <Flex direction="row" justify="between" style={{ width: "100%" }}>
              <Text size="2" weight="medium" style={{ width: "45%" }}>
                b connect location
              </Text>
              <Text size="2" weight="medium" style={{ width: "45%", paddingLeft: "40px" }}>
                Google Business Profile location
              </Text>
            </Flex>

            <Flex direction="column" gap="3">
              {/* All locations */}
              {allLocations.length > 0 ? (
                allLocations.map((loc) => (
                  <Flex key={loc.value} direction="row" justify="between" align="center" style={{ width: "100%" }}>
                    {/* Location name - always read-only */}
                    <Flex style={{ width: "45%", minWidth: 0 }}>
                      <Flex
                        style={{
                          width: "100%",
                          backgroundColor: "var(--gray-3)",
                          border: "1px solid var(--gray-a7)",
                          borderRadius: "var(--radius-2)",
                          padding: "0 var(--space-2)",
                          height: "34px",
                          alignItems: "center",
                          overflow: "hidden",
                        }}
                      >
                        <Text
                          size="2"
                          title={loc.label}
                          style={{
                            textOverflow: "ellipsis",
                            overflow: "hidden",
                            whiteSpace: "nowrap",
                            width: "100%",
                            color: "var(--gray-a11)",
                          }}
                        >
                          {loc.label}
                        </Text>
                      </Flex>
                    </Flex>

                    <Flex align="center" style={{ paddingLeft: "8px", paddingRight: "8px" }}>
                      <IconButton size="2" variant="ghost" color="gray" style={{ pointerEvents: "none" }}>
                        <ArrowRightIcon width="16" height="16" />
                      </IconButton>
                    </Flex>

                    {/* Google Location - either read-only or dropdown */}
                    <Flex style={{ width: "45%", minWidth: 0 }}>
                      {loc.isMapped ? (
                        // Read-only for mapped locations
                        <Flex
                          style={{
                            width: "100%",
                            backgroundColor: "var(--gray-3)",
                            border: "1px solid var(--gray-a7)",
                            borderRadius: "var(--radius-2)",
                            padding: "0 var(--space-2)",
                            height: "34px",
                            alignItems: "center",
                            overflow: "hidden",
                          }}
                        >
                          <Text
                            size="2"
                            title={loc.google_location_name}
                            style={{
                              textOverflow: "ellipsis",
                              overflow: "hidden",
                              whiteSpace: "nowrap",
                              width: "100%",
                              color: "var(--gray-a11)",
                            }}
                          >
                            {loc.google_location_name}
                          </Text>
                        </Flex>
                      ) : isOwner ? (
                        // Dropdown for unmapped locations (only for owners)
                        <Select.Root 
                          value={locationMappings[loc.value] || ""} 
                          onValueChange={(value) => handleGoogleLocationChange(loc.value, value)} 
                          style={{ width: "100%" }}
                        >
                          <Select.Trigger
                            placeholder="Select..."
                            style={{
                              width: "100%",
                              maxWidth: "100%",
                              overflow: "hidden",
                              boxSizing: "border-box",
                            }}
                          >
                            <div
                              style={{
                                textOverflow: "ellipsis",
                                overflow: "hidden",
                                whiteSpace: "nowrap",
                                width: "100%",
                                display: "block",
                                maxWidth: "100%",
                              }}
                              title={
                                locationMappings[loc.value]
                                  ? googleLocations.find((g) => g.name === locationMappings[loc.value])?.title || ""
                                  : "Select a Google location"
                              }
                            >
                              {locationMappings[loc.value]
                                ? googleLocations.find((g) => g.name === locationMappings[loc.value])?.title || ""
                                : "Select a Google location"}
                            </div>
                          </Select.Trigger>
                          <Select.Content side="bottom" align="start" sideOffset={4}>
                            <Select.Item value="none">
                              <Text color="gray">-- No mapping --</Text>
                            </Select.Item>
                            {googleLocations
                              .filter((googleLoc) => {
                                // Get all mapped Google location IDs from existing mappings
                                const mappedGoogleLocationIds = allLocations
                                  .filter((l) => l.isMapped)
                                  .map((l) => {
                                    const mapping = mappingsData?.data?.mappings?.find(
                                      (m) => m.location.toString() === l.value.toString()
                                    );
                                    return mapping?.google_business_location_id;
                                  })
                                  .filter(Boolean);
                                
                                // Also check pending mappings (selected but not yet saved)
                                const pendingMappedIds = Object.values(locationMappings);
                                
                                // Filter out already mapped Google locations
                                const isAlreadyMapped = mappedGoogleLocationIds.includes(googleLoc.fullLocationId) ||
                                  mappedGoogleLocationIds.includes(`locations/${googleLoc.locationId}`);
                                
                                // Filter out pending selections (but allow the current selection)
                                const isPendingForOther = pendingMappedIds.includes(googleLoc.name) && 
                                  locationMappings[loc.value] !== googleLoc.name;
                                
                                return !isAlreadyMapped && !isPendingForOther;
                              })
                              .map((googleLoc) => (
                              <Select.Item key={googleLoc.name} value={googleLoc.name} title={googleLoc.title}>
                                <Flex direction="column">
                                  <Text size="2">{googleLoc.title}</Text>
                                  {googleLoc.address && (
                                    <Text size="1" color="gray">
                                      {googleLoc.address}
                                    </Text>
                                  )}
                                </Flex>
                              </Select.Item>
                            ))}
                          </Select.Content>
                        </Select.Root>
                      ) : (
                        // Read-only for unmapped locations when user is manager
                        <Flex
                          style={{
                            width: "100%",
                            backgroundColor: "var(--gray-3)",
                            border: "1px solid var(--gray-a7)",
                            borderRadius: "var(--radius-2)",
                            padding: "0 var(--space-2)",
                            height: "34px",
                            alignItems: "center",
                            overflow: "hidden",
                          }}
                        >
                          <Text
                            size="2"
                            style={{
                              textOverflow: "ellipsis",
                              overflow: "hidden",
                              whiteSpace: "nowrap",
                              width: "100%",
                              color: "var(--gray-a11)",
                            }}
                          >
                            Not mapped
                          </Text>
                        </Flex>
                      )}
                    </Flex>
                  </Flex>
                ))
              ) : (
                <Flex justify="center" style={{ padding: "20px 0" }}>
                  <Text>No locations found.</Text>
                </Flex>
              )}
            </Flex>

            <Flex justify="end" style={{ marginTop: "var(--space-2)", padding: "var(--space-2)" }}>
              <Button
                size="2"
                variant="solid"
                color="blue"
                onClick={handleSaveMappings}
                loading={isUpdating}
                disabled={!isOwner || isUpdating || !allLocations.some((loc) => !loc.isMapped && locationMappings[loc.value])}
              >
                Save changes
              </Button>
            </Flex>
          </Flex>
        </ActiveIntegrateCard.Section>
      </ActiveIntegrateCard.Root>

      {/* Confirmation Dialog */}
      <Dialog.Root open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <Dialog.Content size="3" style={{ maxWidth: 450 }}>
          <Dialog.Title>Disconnect Google Reviews</Dialog.Title>
          <Dialog.Description size="2" mb="4">
            This will remove all location mappings and stop syncing reviews from Google Business Profile. You will need to reconnect and set up mappings again to resume syncing.
          </Dialog.Description>

          <Flex gap="3" justify="end">
            <Dialog.Close>
              <Button variant="soft" color="gray">
                Cancel
              </Button>
            </Dialog.Close>
            <Button variant="solid" color="red" onClick={handleDeactivate} loading={isDeactivating} disabled={isDeactivating}>
              Disconnect
            </Button>
          </Flex>
        </Dialog.Content>
      </Dialog.Root>

      {/* Permission Alert Dialog */}
      <PermissionAlertDialog
        isOpen={showPermissionAlert}
        onOpenChange={setShowPermissionAlert}
        message="Only owners can perform this action"
      />
    </>
  );
}
